import React from 'react';
import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import { IFiguresAndTablesFile } from '@publishing-engine/domain/models';
import { openFile } from 'src/utils/file';
import { useToast } from '@morressier/alembic';
import { isLocal } from '../../../../utils/env';

interface Props {
  images?: IFiguresAndTablesFile;
}

const GraphicalAbstractImageDisplay: React.FC<Props> = ({ images }) => {
  const toast = useToast();

  const renderFileLink = (file: { url: string; id?: string; name: string }) => (
    <Box
      cursor="pointer"
      onClick={() => {
        openFile(file.url).catch(() => {
          toast({
            variant: 'error',
            title: 'Error',
            description: `Failed to obtain access to the file. ${
              isLocal ? 'Are you port-forwarding to asset-api?' : ''
            }`,
            isClosable: true
          });
        });
      }}
    >
      <Text
        textStyle="textBody"
        color="actionPrimaryFill"
        fontWeight="bold"
        wordBreak="break-word"
        textAlign="initial"
        whiteSpace="normal"
        width="full"
      >
        {file.name}
      </Text>
    </Box>
  );

  const renderCaptionContent = (caption?: string, altText?: string) => (
    <Flex direction="column" gap={2}>
      {caption ? (
        <>
          <Flex align="center" gap={2}>
            <Badge>Caption</Badge>
            <Text textStyle="textBody" wordBreak="break-word" whiteSpace="normal">
              {caption}
            </Text>
          </Flex>
          {altText && (
            <Flex align="center" gap={2}>
              <Badge>Alt-Text</Badge>
              <Text textStyle="textBody" wordBreak="break-word" whiteSpace="normal">
                {altText}
              </Text>
            </Flex>
          )}
        </>
      ) : (
        <Text as="span" color="neutralSecondaryContent">
          No caption provided
        </Text>
      )}
    </Flex>
  );

  const renderSection = (items: IFiguresAndTablesFile) => (
    <Box mb={6}>
      <Flex direction="column" gap={3} display={{ base: 'flex', md: 'none' }}>
        {items.map((item, idx) => {
          const parentFile = item.file ?? null;
          return (
            <React.Fragment key={idx}>
              <Box
                background="white"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
                p={3}
              >
                <Text textStyle="headingItem" mb={2}>
                  Image {idx + 1}
                </Text>
                <Text textStyle="headingItem" fontSize="xs" color="gray.500" mb={1}>
                  Caption & Alt-Text
                </Text>
                {renderCaptionContent(item.caption, item.altText)}
                <Text textStyle="headingItem" fontSize="xs" color="gray.500" mt={3} mb={1}>
                  File
                </Text>
                {parentFile ? renderFileLink(parentFile) : (
                  <Text as="span" color="gray.500">
                    Composite Images Provided
                  </Text>
                )}
              </Box>
              {item.isComposite &&
                Array.isArray(item.subParts) &&
                item.subParts.map((sub, subIdx) => (
                  <Box
                    key={`${idx}-${sub.id}`}
                    background="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                    p={3}
                    ml={4}
                  >
                    <Text textStyle="headingItem" mb={2}>
                      Image {idx + 1}
                      {String.fromCharCode(97 + subIdx)}
                    </Text>
                    <Text textStyle="headingItem" fontSize="xs" color="gray.500" mb={1}>
                      Caption & Alt-Text
                    </Text>
                    {renderCaptionContent(sub.caption, sub.altText)}
                    {sub.file && (
                      <>
                        <Text textStyle="headingItem" fontSize="xs" color="gray.500" mt={3} mb={1}>
                          File
                        </Text>
                        {renderFileLink(sub.file)}
                      </>
                    )}
                  </Box>
                ))}
            </React.Fragment>
          );
        })}
      </Flex>

      <Box borderRadius="md" display={{ base: 'none', md: 'block' }}>
        <Table width="100%">
          <Thead>
            <Tr
              gap={4}
              sx={{
                '& th': {
                  textTransform: 'none',
                  color: 'black',
                  background: 'white'
                }
              }}
            >
              <Th>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Image No.
                </Text>
              </Th>
              <Th whiteSpace="nowrap">
                <Text
                  textStyle="headingItem"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  Caption & Alt-Text
                  <Box as="span" color="red.500">
                    *
                  </Box>
                </Text>
              </Th>
              <Th>
                <Text textStyle="headingItem">
                  File
                  <Box as="span" color="red.500">
                    *
                  </Box>
                </Text>
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {items.map((item, idx) => {
              const parentFile = item.file ?? null;

              return (
                <React.Fragment key={idx}>
                  <Tr background="white">
                    <Td>Image {idx + 1}</Td>
                    <Td>{renderCaptionContent(item.caption, item.altText)}</Td>
                    <Td>
                      {parentFile ? (
                        <Flex direction="column" gap={1}>
                          {renderFileLink(parentFile)}
                        </Flex>
                      ) : (
                        <Text as="span" color="gray.500">
                          Composite Images Provided
                        </Text>
                      )}
                    </Td>
                  </Tr>

                  {item.isComposite &&
                    Array.isArray(item.subParts) &&
                    item.subParts.map((sub, subIdx) => (
                      <Tr key={`${idx}-${sub.id}`} background="white">
                        <Td>
                          <Text as="span" ml={4}>
                            Image {idx + 1}
                            {String.fromCharCode(97 + subIdx)}
                          </Text>
                        </Td>
                        <Td>
                          <Box p={4}>
                            {renderCaptionContent(sub.caption, sub.altText)}
                          </Box>
                        </Td>
                        <Td>
                          {sub.file && (
                            <Flex direction="column" gap={1}>
                              {renderFileLink(sub.file)}
                            </Flex>
                          )}
                        </Td>
                      </Tr>
                    ))}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );

  return <>{images && renderSection(images)}</>;
};

export default GraphicalAbstractImageDisplay;
